import { buildDateWindowPayload, dateWindowError, defaultDateWindow, isPickedWindow } from 'shared/lib/dateWindow';

/** Dates the search period can go by: ETD (default) or ETA, as shown in the result grid. */
export const RATE_SEARCH_DATE_FIELDS = [
    { value: 'etd', label: 'ETD' },
    { value: 'eta', label: 'ETA' },
];
export const RATE_SEARCH_DEFAULT_DATE_FIELD = RATE_SEARCH_DATE_FIELDS[0].value;

/** Longest period the operator may pick, in days: two years, same as the server. */
export const RATE_SEARCH_DATE_WINDOW_MAX_DAYS = 730;

/** The period a search starts with: ETD, the past year up to today. */
export const defaultRateSearchDateWindow = () => ({ field: RATE_SEARCH_DEFAULT_DATE_FIELD, ...defaultDateWindow() });

/** True for the starting period; it always applies, so no removable chip is shown for it. */
export const isDefaultRateSearchDateWindow = (window) => {
    const base = defaultRateSearchDateWindow();
    return (
        (window?.field || RATE_SEARCH_DEFAULT_DATE_FIELD) === base.field &&
        window?.start === base.start &&
        window?.end === base.end
    );
};

/** Why the picked period cannot be searched, or null. Leaving both dates empty means the past year. */
export const rateSearchDateError = ({ start, end } = {}) => {
    if (!start && !end) return null;
    return dateWindowError(start, end, RATE_SEARCH_DATE_WINDOW_MAX_DAYS);
};

/**
 * Picked values as the server reads them: one value goes alone so the previous server
 * version still understands it, several go as a list, none as null.
 */
export const idsPayload = (values) => {
    const ids = (Array.isArray(values) ? values : values ? [values] : []).map((v) => v?.id).filter((id) => id != null);
    if (ids.length === 0) return null;
    return ids.length === 1 ? ids[0] : ids;
};
export const clientIdsPayload = idsPayload;

/**
 * Stops a search with several clients and a Shipper/Consignee: they belong to one client's
 * HBLs, so it would be unclear whose shipper is meant. Returns the reason, or null.
 */
export const clientMultiSelectConflict = ({ clients, shipper, consignee }) => {
    const count = Array.isArray(clients) ? clients.length : clients ? 1 : 0;
    if (count <= 1) return null;
    if (shipper?.id != null) return 'Shipper can be used with a single client only.';
    if (consignee?.id != null) return 'Consignee can be used with a single client only.';
    return null;
};

/**
 * The search request built from the filter bar. The period's date always goes, so "ETA:
 * past year" with no dates picked really searches by ETA.
 */
export const buildRateSearchCriteria = ({
    clients,
    team,
    shipper,
    consignee,
    pol,
    aol,
    fpod,
    deliveryLocation,
    rateTypes,
    dateWindow,
}) => ({
    il_trade_partner_id_client: clientIdsPayload(clients),
    il_team_id: team?.id ?? null,
    il_trade_partner_id_shipper: shipper?.id ?? null,
    il_trade_partner_id_consignee: consignee?.id ?? null,
    il_location_id_pol: pol?.id ?? null,
    il_location_id_aol: aol?.id ?? null,
    il_location_id_fpod: fpod?.id ?? null,
    delivery_location: deliveryLocation?.trim() || null,
    il_rate_type_id: idsPayload(rateTypes),
    ...(isPickedWindow(dateWindow)
        ? buildDateWindowPayload(dateWindow, RATE_SEARCH_DEFAULT_DATE_FIELD)
        : { date_field: dateWindow?.field || RATE_SEARCH_DEFAULT_DATE_FIELD }),
});
